/**
 * Dashboard Page - Landing page showing available applications
 *
 * Displays app cards for authorized applications.
 *
 * Requirements: 1.3
 */

import React, { useMemo } from 'react';
import {
  ChatRegular,
  DocumentSearchRegular,
  SettingsRegular,
  WarningRegular,
} from '@fluentui/react-icons';
import { Navigation, AppCard } from '../../../shared/src/components';
import { usePermissions } from '../../../shared/src/auth';
import type { AppRole } from '../../../shared/src/auth';
import styles from './Dashboard.module.css';

/**
 * Application configuration for dashboard cards
 */
export interface AppConfig {
  id: string;
  title: string;
  description: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  requiredRole: AppRole;
}

/**
 * Available applications configuration
 */
export const APP_CONFIGS: AppConfig[] = [
  {
    id: 'policy_navigator',
    title: 'Policy Navigator',
    description: 'Query UK drone regulations with AI-powered search',
    href: '/policy-navigator',
    icon: ChatRegular,
    requiredRole: 'policy_navigator',
  },
  {
    id: 'evidence_extractor',
    title: 'Evidence Extractor',
    description: 'Extract evidence from regulatory documents',
    href: '/evidence-extractor',
    icon: DocumentSearchRegular,
    requiredRole: 'evidence_extractor',
  },
  {
    id: 'admin',
    title: 'Administration',
    description: 'Manage users and application access',
    href: '/admin',
    icon: SettingsRegular,
    requiredRole: 'admin',
  },
];

/**
 * Get available apps based on user roles
 * Pure function for testability
 *
 * @param userRoles - Array of roles the user has
 * @param appConfigs - Array of app configurations
 * @returns Array of apps the user can access
 */
export function getAvailableApps(
  userRoles: AppRole[],
  appConfigs: AppConfig[] = APP_CONFIGS
): AppConfig[] {
  return appConfigs.filter((app) => userRoles.includes(app.requiredRole));
}

/**
 * Dashboard page component
 *
 * Features:
 * - Displays app cards for each authorized application (Req 1.3)
 * - Shows welcome message with user name
 */
export const Dashboard: React.FC = () => {
  const { user, hasRole } = usePermissions();

  const availableApps = useMemo(() => {
    if (!user) return [];
    return getAvailableApps(user.roles);
  }, [user]);

  return (
    <div className={styles.container} data-testid="dashboard-page">
      <Navigation currentApp="landing" showBreadcrumb={false} />
      <main className={styles.main}>
        <div className={styles.welcomeSection}>
          <h1 className={styles.title}>
            Welcome{user?.name ? `, ${user.name}` : ''}
          </h1>
          <p className={styles.subtitle}>
            {availableApps.length > 0
              ? 'Welcome to the CAA UAS AI Assistant Tools. Select an application to get started:'
              : 'Welcome to the CAA UAS AI Assistant Tools. Contact your administrator for application access.'}
          </p>
        </div>

        <div className={styles.prototypeWarning} data-testid="prototype-warning">
          <WarningRegular className={styles.warningIcon} />
          <p>
            These applications are <strong>prototypes</strong> and are provided for evaluation purposes only.
            Features, accuracy, and availability may change without notice. Do not rely on these tools
            for operational or regulatory decisions.
          </p>
        </div>

        {availableApps.length > 0 ? (
          <div className={styles.appGrid} data-testid="app-grid">
            {availableApps.map((app) => (
              <AppCard
                key={app.id}
                id={app.id}
                title={app.title}
                description={app.description}
                href={app.href}
                icon={app.icon}
              />
            ))}
          </div>
        ) : (
          <div className={styles.noApps} data-testid="no-apps-message">
            <h2 className={styles.noAppsTitle}>No Applications Available</h2>
            <p className={styles.noAppsText}>
              You don't have access to any applications yet. Please contact your
              administrator to request access.
            </p>
          </div>
        )}

        <div className={styles.disclaimer} data-testid="disclaimer">
          <p>
            These AI Assistant tools are provided by the{' '}
            <strong>UK Civil Aviation Authority (CAA)</strong> to support users
            in accessing information and guidance. Responses generated by this
            system are for{' '}
            <strong>general informational purposes only</strong> and{' '}
            <strong>
              do not constitute legal advice, regulatory advice, or an official
              determination by the CAA
            </strong>
            . In the event of any discrepancy, published CAA regulations,
            official guidance, and formally issued communications take
            precedence.
          </p>
          <p>
            While the Assistant is designed to provide accurate and helpful
            information, responses{' '}
            <strong>may be incomplete, inaccurate, or out of date</strong>.
            Users must <strong>independently verify information</strong>.
            This service is provided on an{' '}
            <strong>"as available"</strong> basis, and users remain{' '}
            <strong>
              fully responsible for ensuring compliance with all applicable
              legislation and regulatory requirements
            </strong>
            .
          </p>
          <p>
            Please{' '}
            <strong>
              do not submit personal, confidential, or commercially sensitive
              information
            </strong>{' '}.
          </p>
        </div>
      </main>
    </div>
  );
};
